import { useIntl } from 'react-intl'

import { getPlainText } from '../../utils/utils'
import { useLocale } from '../../../../../../../../hooks/use-locale'
import {
  QuoteSectionClientProposalPlanningItemFragment,
  QuoteSectionClientProposalPricingItemFragment,
} from '../../../../../../../../lib/graphql'
import {
  decimalPrice,
  percentage,
  provisionUnitToLabel,
} from '../../../../../../../../utils'
import { divideByFactor } from '../../../../../../../../utils/prices'
import {
  useCurrencyAreaContext,
  VAT_STATUS,
} from '../../../../../../../contexts'
import { useVatStatus } from '../../../../../../../contexts/vat-status-context/useVatStatus'

const getVatRates = (item: QuoteSectionClientProposalPricingItemFragment) => {
  if (item.objectData.prices.length === 0) {
    return '-'
  }
  const price =
    item.objectData.prices.find(
      (price) => price._id.toString() === item.option.priceId?.toString(),
    ) ?? item.objectData.prices[0]

  const vatRates = price.vatPrices
    .map((vatPrice) => vatPrice.vatRate)
    .filter(
      (vatRate): vatRate is number => vatRate !== undefined && vatRate !== null,
    ) // Type guard
    .sort((a, b) => a - b)

  return vatRates.map((vatRate) => divideByFactor(vatRate)).join(' - ') + ' %'
}

export const useGetFormattedClientPricingItems = () => {
  const intl = useIntl()
  const { locale } = useLocale()
  const { vatStatus } = useVatStatus()
  const { currency } = useCurrencyAreaContext()

  const getFormattedClientPricingItems = ({
    clientPricingItems,
  }: {
    clientPricingItems: QuoteSectionClientProposalPricingItemFragment[]
  }) => {
    return clientPricingItems.map((item) => {
      return {
        id: item._id,
        label: item.objectData.label,
        description: getPlainText(item.objectData.description ?? ''),
        commission:
          percentage({ rate: item.priceOption.fees?.ownerFeesRate, locale }) ??
          '-',
        quantity: item.priceOption.quantity,
        category: item.category,
        unit: item.priceUnit
          ? provisionUnitToLabel({ unit: item.priceUnit, intl })
          : '-',
        unitPrice:
          vatStatus === VAT_STATUS.HT
            ? decimalPrice(item.unitPrice?.basePrice?.priceWithoutVat, {
                currency,
              })
            : decimalPrice(item.unitPrice?.basePrice?.priceWithVat, {
                currency,
              }),
        vatRate: getVatRates(item),
        totalPriceHT: decimalPrice(
          item.totalPrice?.basePrice?.priceWithoutVat,
          { currency },
        ),
        totalPriceTTC: decimalPrice(item.totalPrice?.basePrice?.priceWithVat, {
          currency,
        }),
        unitPriceWithDiscount:
          vatStatus === VAT_STATUS.HT
            ? decimalPrice(item.unitPrice?.priceWithDiscount?.priceWithoutVat, {
                currency,
              })
            : decimalPrice(item.unitPrice?.priceWithDiscount?.priceWithVat, {
                currency,
              }),
        totalPriceHTWithDiscount: decimalPrice(
          item.totalPrice?.priceWithDiscount?.priceWithoutVat,
          { currency },
        ),
        totalPriceTTCWithDiscount: decimalPrice(
          item.totalPrice?.priceWithDiscount?.priceWithVat,
          { currency },
        ),
        adjustmentStatus: item.adjustmentStatus ?? undefined,
      }
    })
  }

  const getFormattedPlanningItems = ({
    planningItems,
  }: {
    planningItems: QuoteSectionClientProposalPlanningItemFragment[]
  }) => {
    return planningItems.map((item) => {
      return {
        id: item.clientPricingItem._id,
        label: item.clientPricingItem.objectData.label,
        description: getPlainText(
          item.clientPricingItem.objectData.description ?? '',
        ),
        commission:
          percentage({
            rate: item.clientPricingItem.priceOption.fees?.ownerFeesRate,
            locale,
          }) ?? '-',
        quantity: item.clientPricingItem.priceOption.quantity,
        category: item.clientPricingItem.category,
        unit: item.clientPricingItem.priceUnit
          ? provisionUnitToLabel({
              unit: item.clientPricingItem.priceUnit,
              intl: useIntl(),
            })
          : '-',
        unitPrice:
          vatStatus === VAT_STATUS.HT
            ? decimalPrice(
                item.clientPricingItem.unitPrice?.basePrice?.priceWithoutVat,
                { currency },
              )
            : decimalPrice(
                item.clientPricingItem.unitPrice?.basePrice?.priceWithVat,
                { currency },
              ),
        vatRate: getVatRates(item.clientPricingItem),
        totalPriceHT: decimalPrice(
          item.clientPricingItem.totalPrice?.basePrice?.priceWithoutVat,
          { currency },
        ),
        totalPriceTTC: decimalPrice(
          item.clientPricingItem.totalPrice?.basePrice?.priceWithVat,
          { currency },
        ),
        unitPriceWithDiscount:
          vatStatus === VAT_STATUS.HT
            ? decimalPrice(
                item.clientPricingItem.unitPrice?.priceWithDiscount
                  ?.priceWithoutVat,
                { currency },
              )
            : decimalPrice(
                item.clientPricingItem.unitPrice?.priceWithDiscount
                  ?.priceWithVat,
                { currency },
              ),
        totalPriceHTWithDiscount: decimalPrice(
          item.clientPricingItem.totalPrice?.priceWithDiscount?.priceWithoutVat,
          {
            currency,
          },
        ),
        totalPriceTTCWithDiscount: decimalPrice(
          item.clientPricingItem.totalPrice?.priceWithDiscount?.priceWithVat,
          {
            currency,
          },
        ),
        adjustmentStatus: item.clientPricingItem.adjustmentStatus ?? undefined,
      }
    })
  }

  return { getFormattedClientPricingItems, getFormattedPlanningItems }
}
